import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const STEPS = [
  { k: "scope", l: "Scope", q: "What are you building?" },
  { k: "stage", l: "Stage", q: "Where are you in the journey?" },
  { k: "details", l: "Details", q: "Tell us more." },
];

const SCOPES = ["Web app", "Mobile app", "AI agents", "Cyber security", "Other"];
const STAGES = ["Idea", "Prototype", "In production", "Scaling"];

export function MultiStepForm({ testId = "multi-form" }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ scope: "", stage: "", message: "" });
  const [done, setDone] = useState(false);

  const next = () => {
    if (step === 0 && !data.scope) return toast.error("Pick a scope.");
    if (step === 1 && !data.stage) return toast.error("Pick a stage.");
    if (step === 2 && !data.message.trim()) return toast.error("Add a short message.");
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else {
      setDone(true);
      toast.success("Brief saved (showcase only).");
    }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const progress = ((step + (done ? 1 : 0)) / STEPS.length) * 100;

  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-hairline bg-glass backdrop-blur-2xl p-8 md:p-10"
    >
      <div className="flex items-center justify-between text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x mb-6">
        <span>Step {Math.min(step + 1, STEPS.length)} / {STEPS.length}</span>
        <span>{STEPS[step].l}</span>
      </div>
      <div className="h-1 w-full rounded-full bg-hairline overflow-hidden mb-10">
        <motion.div
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="h-full"
          style={{ background: "var(--text-primary)" }}
        />
      </div>

      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div
            key={step}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -40, opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <h4 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-primary-x">
              {STEPS[step].q}
            </h4>

            <div className="mt-8">
              {step === 0 && (
                <div className="flex flex-wrap gap-2">
                  {SCOPES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setData((d) => ({ ...d, scope: s }))}
                      data-testid={`${testId}-scope-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                        data.scope === s
                          ? "border-transparent text-canvas"
                          : "border-hairline-strong text-primary-x hover:bg-glass-hover"
                      }`}
                      style={
                        data.scope === s
                          ? { background: "var(--text-primary)", color: "var(--canvas)" }
                          : {}
                      }
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {step === 1 && (
                <div className="grid grid-cols-2 gap-3">
                  {STAGES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setData((d) => ({ ...d, stage: s }))}
                      data-testid={`${testId}-stage-${s.toLowerCase()}`}
                      className={`rounded-xl border px-4 py-4 text-left transition-colors ${
                        data.stage === s
                          ? "border-transparent text-canvas"
                          : "border-hairline-strong text-primary-x hover:bg-glass-hover"
                      }`}
                      style={
                        data.stage === s
                          ? { background: "var(--text-primary)", color: "var(--canvas)" }
                          : {}
                      }
                    >
                      <div className="font-display text-lg">{s}</div>
                    </button>
                  ))}
                </div>
              )}
              {step === 2 && (
                <textarea
                  value={data.message}
                  onChange={(e) => setData((d) => ({ ...d, message: e.target.value }))}
                  data-testid={`${testId}-message`}
                  rows={5}
                  placeholder="What's the hardest part?"
                  className="w-full rounded-xl border border-hairline-strong bg-glass px-4 py-3 text-primary-x placeholder:text-muted-x outline-none focus:border-primary-x transition-colors"
                  style={{ borderColor: "var(--hairline-strong)" }}
                />
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="done"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="py-8"
          >
            <div className="font-display text-3xl md:text-4xl text-primary-x">All set. ✓</div>
            <p className="mt-4 text-secondary-x">This is a showcase form — your brief was not actually sent.</p>
            <pre className="mt-6 rounded-xl border border-hairline bg-surface-2 p-4 text-xs text-secondary-x overflow-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          </motion.div>
        )}
      </AnimatePresence>

      {!done && (
        <div className="mt-10 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="text-sm text-secondary-x disabled:opacity-40 hover:text-primary-x transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={next}
            data-testid={`${testId}-next`}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
          >
            {step === STEPS.length - 1 ? "Submit" : "Continue"}
            <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** Floating-label input with animated underline. */
export function FloatingInput({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="relative block">
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        placeholder=" "
        className="peer w-full bg-transparent border-b border-hairline-strong text-primary-x py-3 outline-none focus:border-transparent transition-colors"
      />
      <span className="pointer-events-none absolute left-0 top-3 text-secondary-x text-sm transition-all peer-focus:-translate-y-5 peer-focus:text-[11px] peer-focus:tracking-[0.18em] peer-focus:uppercase peer-focus:text-muted-x peer-[&:not(:placeholder-shown)]:-translate-y-5 peer-[&:not(:placeholder-shown)]:text-[11px] peer-[&:not(:placeholder-shown)]:tracking-[0.18em] peer-[&:not(:placeholder-shown)]:uppercase peer-[&:not(:placeholder-shown)]:text-muted-x">
        {label}
      </span>
      <span className="absolute left-0 bottom-0 h-px w-0 peer-focus:w-full transition-all duration-300" style={{ background: "var(--text-primary)" }} />
    </label>
  );
}
